'use client'
import React from 'react'
import { Box, Button, ClickAwayListener, IconButton } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'

import DatePicker from '@/src/components/desktop/srp/searchHeader/searchModifierInputs/CalendarWrapper/DatePicker'
import { FromToInput } from '@/src/components/desktop/srp/searchHeader/searchModifierInputs/fromToInput'
import { TravelersInput } from '@/src/components/desktop/srp/searchHeader/searchModifierInputs/TravelersInput'
import { MultiCitySegment } from '@/src/components/desktop/srp/searchHeader/searchModifierInputs/hooks/search-inputs-manager'
import { TranslationFunction } from '@/src/types/translations'

export interface FirstSegmentRowProps {
    segment: MultiCitySegment
    segmentIndex: number
    segmentsWithSameAirportError: Set<number>
    travelersState: { adults: number; childs: number; infants: number }
    flightClass: string
    isSegmentFieldActive: (segmentIndex: number, field: 'from' | 'to' | 'date') => boolean
    isComponentActive: (component: 'from' | 'to' | 'date' | 'travelers' | null) => boolean
    handleComponentActivation: (component: 'from' | 'to' | 'date' | 'travelers' | null) => void
    onSegmentFieldActivation: (segmentIndex: number, field: 'from' | 'to' | 'date') => void
    onAirportSelect: (segmentIndex: number, value: string, action: 'from' | 'to') => void
    onDateSelect: (segmentIndex: number, dates: any, range: any) => void
    onSwap: (segmentIndex: number) => void
    onSearch: () => void
    getAirportsForSegment: (query: string, segmentIndex: number, field: 'from' | 'to') => void
    closeActiveComponent: () => void
    closeActiveSegmentComponent: () => void
    handleSegment0ClickAway: (event: Event | React.SyntheticEvent) => void
    handleTravelerChange: (type: 'adults' | 'childs' | 'infants', delta: number) => void
    handleFlightClassChange: (newClass: string) => void
    canIncreaseAdults: boolean
    canIncreaseChildren: boolean
    canIncreaseInfants: boolean
    canDecreaseAdults: boolean
    canDecreaseChildren: boolean
    canDecreaseInfants: boolean
    getMinDateForSegment: (segmentIndex: number) => Date | null
    t: TranslationFunction<'common'>
}

export const FirstSegmentRow: React.FC<FirstSegmentRowProps> = ({
    segment,
    segmentIndex,
    segmentsWithSameAirportError,
    travelersState,
    flightClass,
    isSegmentFieldActive,
    isComponentActive,
    handleComponentActivation,
    onSegmentFieldActivation,
    onAirportSelect,
    onDateSelect,
    onSwap,
    onSearch,
    getAirportsForSegment,
    closeActiveComponent,
    closeActiveSegmentComponent,
    handleSegment0ClickAway,
    handleTravelerChange,
    handleFlightClassChange,
    canIncreaseAdults,
    canIncreaseChildren,
    canIncreaseInfants,
    canDecreaseAdults,
    canDecreaseChildren,
    canDecreaseInfants,
    getMinDateForSegment,
    t
}) => {
    // Get minDate for this segment (segment 0 has no minDate)
    const minDate = getMinDateForSegment(segmentIndex)
    return (
        <ClickAwayListener onClickAway={handleSegment0ClickAway}>
            <Box
                data-search-container
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    overflow: 'hidden',
                    bgcolor: '#ffffff',
                    position: 'relative',
                    zIndex: 1,
                    mb: 2
                }}
            >
                {/* Origin - Segment 0 */}
                <Box sx={{
                    border: 1,
                    borderColor: 'grey.300',
                    borderRight: '1px solid rgb(229 231 235)',
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: 70,
                    maxHeight: 70,
                    py: 2,
                    borderTopLeftRadius: '0.75rem',
                    borderBottomLeftRadius: '0.75rem',
                    position: 'relative',
                    flex: 1
                }}>
                    <FromToInput
                        srpResultsLoading={false}
                        key={`${segment?.from.value}-from-0`}
                        action="from"
                        initialValue={segment?.from.value || ''}
                        loading={segment?.from.loading || false}
                        optionListItems={segment?.from.options || []}
                        queryOptionListItems={(query) => getAirportsForSegment(query, 0, 'from')}
                        onOptionSelect={(value) => onAirportSelect(0, value, 'from')}
                        active={isSegmentFieldActive(0, 'from')}
                        onActivate={() => onSegmentFieldActivation(0, 'from')}
                        onClose={closeActiveSegmentComponent}
                        hasError={segmentsWithSameAirportError.has(0)}
                        isPopularAirports={segment?.from.isPopularAirports || false}
                    />
                    
                    {/* Swap Icon - Positioned at the right border center */}
                    <Box
                        sx={{
                            position: 'absolute',
                            right: -18,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 2
                        }}
                    >
                        <IconButton 
                            sx={{ 
                                border: '1px solid rgb(229 231 235)',
                                borderRadius: '50%',
                                width: 36,
                                height: 36,
                                bgcolor: '#ffffff',
                                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                                '&:hover': {
                                    bgcolor: '#f9fafb'
                                }
                            }} 
                            size="small" 
                            onClick={() => onSwap(0)}
                        >
                            <SwapHorizIcon sx={{ fontSize: 18, color: 'grey.600' }} />
                        </IconButton>
                    </Box>
                </Box>

                {/* Destination - Segment 0 */}
                <Box sx={{
                    border: 1,
                    borderColor: 'grey.300',
                    borderLeft: '1px solid rgb(229 231 235)',
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: 70,
                    maxHeight: 70,
                    py: 2,
                    position: 'relative',
                    flex: 1
                }}>
                    <FromToInput
                        key={`${segment?.to.value}-to-0`}
                        action="to"
                        initialValue={segment?.to.value || ''}
                        loading={segment?.to.loading || false}
                        optionListItems={segment?.to.options || []}
                        queryOptionListItems={(query) => getAirportsForSegment(query, 0, 'to')}
                        onOptionSelect={(value) => onAirportSelect(0, value, 'to')}
                        active={isSegmentFieldActive(0, 'to')}
                        onActivate={() => onSegmentFieldActivation(0, 'to')}
                        onClose={closeActiveSegmentComponent}
                        hasError={segmentsWithSameAirportError.has(0)}
                        isPopularAirports={segment?.to.isPopularAirports || false}
                    />
                </Box>

                {/* Date - Segment 0 */}
                <Box sx={{
                    border: 1,
                    borderColor: 'grey.300',
                    borderLeft: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 70,
                    maxHeight: 70,
                    flex: 1,
                    p: 2
                }}>
                    <DatePicker
                        isRoundTrip={false}
                        range={{
                            from: segment?.date || new Date(),
                            to: segment?.date || new Date(),
                            enteredTo: segment?.date || new Date()
                        }}
                        dateClick={(dates, range) => onDateSelect(0, dates, range)}
                        active={isSegmentFieldActive(0, 'date')}
                        onActivate={() => onSegmentFieldActivation(0, 'date')}
                        onClose={closeActiveSegmentComponent}
                        showAddReturn={false}
                        minDate={minDate}
                    />
                </Box>

                {/* Travelers */}
                <Box sx={{
                    border: 1,
                    borderColor: 'grey.300',
                    borderLeft: 0,
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: 70,
                    maxHeight: 70,
                    p: 2,
                    flex: 1
                }}>
                    <TravelersInput
                        adults={travelersState.adults}
                        childs={travelersState.childs}
                        infants={travelersState.infants}
                        flightClass={flightClass}
                        onTravelerChange={handleTravelerChange}
                        onFlightClassChange={handleFlightClassChange}
                        canIncreaseAdults={canIncreaseAdults}
                        canIncreaseChildren={canIncreaseChildren}
                        canIncreaseInfants={canIncreaseInfants}
                        canDecreaseAdults={canDecreaseAdults}
                        canDecreaseChildren={canDecreaseChildren}
                        canDecreaseInfants={canDecreaseInfants}
                        active={isComponentActive('travelers')}
                        onActivate={() => handleComponentActivation('travelers')}
                        onClose={closeActiveComponent}
                    />
                </Box>

                {/* Search Button */}
                <Box sx={{
                    border: 1,
                    borderColor: 'grey.300',
                    borderLeft: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 70,
                    maxHeight: 70,
                    p: 2,
                    borderTopRightRadius: '0.75rem',
                    borderBottomRightRadius: '0.75rem',
                    flex: 0.5
                }}>
                    <Button
                        variant="contained"
                        startIcon={<SearchIcon sx={{ color: 'white' }} />}
                        sx={{ 
                            borderRadius: '0.75rem',
                            p: 1.5,
                            minWidth: 100,
                            bgcolor: '#2C57A0',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            '&:hover': {
                                bgcolor: '#1e3d73'
                            }
                        }}
                        onClick={onSearch}
                    >
                        {t('search')}
                    </Button>
                </Box>
            </Box>
        </ClickAwayListener>
    )
}

